/**
 * PricingModal Component (F16) - v2.0 NOWPayments Integration
 * Token purchase modal with USDT (TRC20) payment via NOWPayments
 *
 * Packages (from API):
 * - Starter: $10 → 50 tokens
 * - Pro: $35 → 200 + 20 bonus tokens (Featured)
 * - Enterprise: $75 → 500 + 75 bonus tokens
 *
 * Payment Method: USDT (TRC20) via NOWPayments
 *
 * v2.0 Changes:
 * - Uses new DataFeed payment API (paymentApi.js)
 * - Single payment method (USDT TRC20) - no provider toggle
 * - Redirects to NOWPayments invoice_url
 * - Stores payment_id in localStorage for success page
 * - Shows bonus tokens separately in green
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Box,
    Typography,
    Button,
    IconButton,
    Chip,
    CircularProgress,
    Alert
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import BoltIcon from '@mui/icons-material/Bolt';
import CheckIcon from '@mui/icons-material/Check';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { getPackages, createPayment, storePendingPayment } from '../../api/paymentApi';

/**
 * Fallback packages if API fails
 */
const FALLBACK_PACKAGES = [
    { id: 1, name: 'Starter', tokens: 50, bonusTokens: 0, totalTokens: 50, priceUsd: 10, isFeatured: false },
    { id: 2, name: 'Pro', tokens: 200, bonusTokens: 20, totalTokens: 220, priceUsd: 35, isFeatured: true },
    { id: 3, name: 'Enterprise', tokens: 500, bonusTokens: 75, totalTokens: 575, priceUsd: 75, isFeatured: false }
];

/**
 * PricingModal Component
 * @param {boolean} open - Modal visibility
 * @param {function} onClose - Close handler
 * @param {function} onSelectTier - Legacy tier selection handler (optional)
 * @param {number} currentBalance - User's current token balance
 * @param {string} token - JWT token for API calls (optional, uses localStorage)
 */
const PricingModal = ({
    open = false,
    onClose,
    onSelectTier,
    currentBalance = 0,
    token
}) => {
    const navigate = useNavigate();
    const [selectedPackageId, setSelectedPackageId] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [packages, setPackages] = useState(FALLBACK_PACKAGES);

    // Fetch packages when modal opens
    useEffect(() => {
        if (open) {
            fetchPackages();
        }
    }, [open]);

    // Auto-select featured package on load
    useEffect(() => {
        if (packages.length > 0 && selectedPackageId === null) {
            const featured = packages.find(p => p.isFeatured);
            setSelectedPackageId(featured?.id || packages[0]?.id);
        }
    }, [packages, selectedPackageId]);

    const fetchPackages = async () => {
        setIsLoading(true);
        setError(null);
        try {
            const result = await getPackages();
            if (result.success && result.data?.packages?.length > 0) {
                setPackages(result.data.packages);
            } else {
                console.warn('[PricingModal] Using fallback packages');
                setPackages(FALLBACK_PACKAGES);
            }
        } catch (err) {
            console.error('[PricingModal] Failed to fetch packages:', err);
            setPackages(FALLBACK_PACKAGES);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSelectPackage = (packageId) => {
        setSelectedPackageId(packageId);
        setError(null);
    };

    const handlePurchase = async () => {
        if (!selectedPackageId || isProcessing) return;

        setIsProcessing(true);
        setError(null);

        try {
            const result = await createPayment(selectedPackageId);

            if (!result.success) {
                setError(result.error || 'Failed to create payment');
                return;
            }

            // Store payment info for success page
            storePendingPayment(result.data.payment_id, result.data.tokens);

            console.log('[PricingModal] Redirecting to NOWPayments:', {
                payment_id: result.data.payment_id,
                tokens: result.data.tokens
            });

            // Redirect to NOWPayments invoice page
            window.location.href = result.data.invoice_url;

        } catch (err) {
            console.error('[PricingModal] Purchase failed:', err);
            setError(err.message || 'Failed to process payment. Please try again.');
        } finally {
            setIsProcessing(false);
        }
    };

    const selectedPackage = packages.find(p => p.id === selectedPackageId);

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    backgroundColor: '#0F0F14',
                    backgroundImage: 'none',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    borderRadius: '16px',
                    maxWidth: '720px',
                    maxHeight: '90vh',
                    display: 'flex',
                    flexDirection: 'column'
                }
            }}
        >
            {/* ════════════════════════════════════════════════════════════════════════
                HEADER
                ════════════════════════════════════════════════════════════════════════ */}
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '24px 24px 16px',
                    borderBottom: '1px solid rgba(255, 255, 255, 0.08)'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                        sx={{
                            width: 40,
                            height: 40,
                            borderRadius: '10px',
                            background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        <BoltIcon sx={{ color: '#FFFFFF', fontSize: '1.5rem' }} />
                    </Box>
                    <Box>
                        <Typography
                            sx={{
                                color: '#FFFFFF',
                                fontSize: '1.25rem',
                                fontWeight: 600,
                                fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                            }}
                        >
                            Get More Tokens
                        </Typography>
                        <Typography
                            sx={{
                                color: 'rgba(255, 255, 255, 0.5)',
                                fontSize: '0.875rem'
                            }}
                        >
                            Current balance: {currentBalance} tokens
                        </Typography>
                    </Box>
                </Box>

                <IconButton
                    onClick={onClose}
                    disabled={isProcessing}
                    sx={{
                        color: 'rgba(255, 255, 255, 0.5)',
                        '&:hover': {
                            color: '#FFFFFF',
                            backgroundColor: 'rgba(255, 255, 255, 0.08)'
                        }
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            {/* ════════════════════════════════════════════════════════════════════════
                CONTENT
                ════════════════════════════════════════════════════════════════════════ */}
            <DialogContent sx={{ padding: '24px', paddingTop: '16px', overflowY: 'auto', flex: 1 }}>
                {isLoading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress sx={{ color: '#7c3aed' }} />
                    </Box>
                ) : (
                    <>
                        {/* Error Alert */}
                        {error && (
                            <Alert
                                severity="error"
                                onClose={() => setError(null)}
                                sx={{
                                    mb: 2,
                                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                    color: '#EF4444',
                                    '& .MuiAlert-icon': { color: '#EF4444' }
                                }}
                            >
                                {error}
                            </Alert>
                        )}

                        {/* Package Cards */}
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                                gap: 2,
                                mt: 1.5
                            }}
                        >
                            {packages.map((pkg) => (
                                <PackageCard
                                    key={pkg.id}
                                    package={pkg}
                                    isSelected={selectedPackageId === pkg.id}
                                    onSelect={() => handleSelectPackage(pkg.id)}
                                    disabled={isProcessing}
                                />
                            ))}
                        </Box>

                        {/* Token Usage Info */}
                        <Box
                            sx={{
                                mt: 2,
                                padding: '16px',
                                backgroundColor: 'rgba(124, 58, 237, 0.08)',
                                borderRadius: '12px',
                                border: '1px solid rgba(124, 58, 237, 0.2)'
                            }}
                        >
                            <Typography
                                sx={{
                                    color: '#FFFFFF',
                                    fontSize: '0.875rem',
                                    fontWeight: 600,
                                    marginBottom: 1
                                }}
                            >
                                Token Usage Guide
                            </Typography>
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                <TokenUsageRow label="Chat message" cost={5} />
                                <TokenUsageRow label="EA Analysis" cost={10} />
                                <TokenUsageRow label="Trading Signals" cost={15} />
                            </Box>
                        </Box>

                        {/* Payment Method Info */}
                        <Box
                            sx={{
                                mt: 2,
                                padding: '16px',
                                backgroundColor: 'rgba(39, 181, 109, 0.08)',
                                borderRadius: '12px',
                                border: '1px solid rgba(39, 181, 109, 0.2)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2
                            }}
                        >
                            <Box
                                sx={{
                                    width: 40,
                                    height: 40,
                                    borderRadius: '10px',
                                    backgroundColor: 'rgba(39, 181, 109, 0.15)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                }}
                            >
                                <AccountBalanceWalletIcon sx={{ color: '#27B56D', fontSize: '1.25rem' }} />
                            </Box>
                            <Box>
                                <Typography
                                    sx={{
                                        color: '#FFFFFF',
                                        fontSize: '0.875rem',
                                        fontWeight: 600
                                    }}
                                >
                                    Payment: USDT (TRC20)
                                </Typography>
                                <Typography
                                    sx={{
                                        color: 'rgba(255, 255, 255, 0.5)',
                                        fontSize: '0.8125rem'
                                    }}
                                >
                                    Secure cryptocurrency payment via NOWPayments
                                </Typography>
                            </Box>
                        </Box>
                    </>
                )}
            </DialogContent>

            {/* ════════════════════════════════════════════════════════════════════════
                FOOTER
                ════════════════════════════════════════════════════════════════════════ */}
            <DialogActions
                sx={{
                    padding: '16px 24px 24px',
                    borderTop: '1px solid rgba(255, 255, 255, 0.08)',
                    justifyContent: 'space-between'
                }}
            >
                <Button
                    onClick={() => {
                        onClose();
                        navigate('/q-ai/payments/history');
                    }}
                    disabled={isProcessing}
                    startIcon={<ReceiptLongIcon sx={{ fontSize: '1rem' }} />}
                    sx={{
                        color: 'rgba(255, 255, 255, 0.5)',
                        textTransform: 'none',
                        fontSize: '0.8125rem',
                        '&:hover': {
                            color: 'rgba(255, 255, 255, 0.7)',
                            backgroundColor: 'rgba(255, 255, 255, 0.05)'
                        }
                    }}
                >
                    Transaction History
                </Button>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                        onClick={onClose}
                        disabled={isProcessing}
                        sx={{
                            color: 'rgba(255, 255, 255, 0.7)',
                            textTransform: 'none',
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.08)'
                            }
                        }}
                    >
                        Cancel
                    </Button>
                <Button
                    onClick={handlePurchase}
                    disabled={isProcessing || isLoading || !selectedPackageId}
                    sx={{
                        background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 100%)',
                        color: '#FFFFFF',
                        textTransform: 'none',
                        fontWeight: 600,
                        padding: '10px 24px',
                        borderRadius: '8px',
                        minWidth: '180px',
                        '&:hover': {
                            background: 'linear-gradient(135deg, #6d28d9 0%, #9333ea 100%)'
                        },
                        '&.Mui-disabled': {
                            background: 'rgba(124, 58, 237, 0.3)',
                            color: 'rgba(255, 255, 255, 0.5)'
                        }
                    }}
                >
                    {isProcessing ? (
                        <>
                            <CircularProgress size={16} sx={{ color: '#FFFFFF', mr: 1 }} />
                            Processing...
                        </>
                    ) : selectedPackage ? (
                        `Buy ${selectedPackage.totalTokens} Tokens - $${selectedPackage.priceUsd}`
                    ) : (
                        'Select a Package'
                    )}
                </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

/**
 * Package Card Component
 * Displays a single token package with price, tokens, and bonus
 */
const PackageCard = ({ package: pkg, isSelected, onSelect, disabled }) => {
    const hasBonus = (pkg.bonusTokens || 0) > 0;
    const pricePerToken = pkg.priceUsd && pkg.totalTokens
        ? (pkg.priceUsd / pkg.totalTokens * 100).toFixed(1)
        : '0.0';

    return (
        <Box
            onClick={disabled ? undefined : onSelect}
            sx={{
                position: 'relative',
                padding: '24px',
                borderRadius: '12px',
                border: isSelected
                    ? '2px solid #7c3aed'
                    : '1px solid rgba(255, 255, 255, 0.1)',
                backgroundColor: isSelected
                    ? 'rgba(124, 58, 237, 0.1)'
                    : 'rgba(255, 255, 255, 0.02)',
                cursor: disabled ? 'not-allowed' : 'pointer',
                opacity: disabled ? 0.6 : 1,
                transition: 'all 0.2s ease',
                '&:hover': disabled ? {} : {
                    borderColor: isSelected ? '#7c3aed' : 'rgba(124, 58, 237, 0.5)',
                    backgroundColor: isSelected
                        ? 'rgba(124, 58, 237, 0.15)'
                        : 'rgba(124, 58, 237, 0.05)'
                }
            }}
        >
            {/* Featured Badge */}
            {pkg.isFeatured && (
                <Chip
                    label="Best Value"
                    size="small"
                    sx={{
                        position: 'absolute',
                        top: -10,
                        right: 12,
                        backgroundColor: '#7c3aed',
                        color: '#FFFFFF',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        height: '20px'
                    }}
                />
            )}

            {/* Selection Indicator */}
            {isSelected && (
                <Box
                    sx={{
                        position: 'absolute',
                        top: 12,
                        left: 12,
                        width: 20,
                        height: 20,
                        borderRadius: '50%',
                        backgroundColor: '#7c3aed',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    <CheckIcon sx={{ color: '#FFFFFF', fontSize: '0.875rem' }} />
                </Box>
            )}

            {/* Package Name */}
            <Typography
                sx={{
                    color: '#FFFFFF',
                    fontSize: '1rem',
                    fontWeight: 600,
                    marginBottom: 1,
                    textAlign: 'center'
                }}
            >
                {pkg.name}
            </Typography>

            {/* Price */}
            <Box sx={{ textAlign: 'center', marginBottom: 2 }}>
                <Typography
                    component="span"
                    sx={{
                        color: '#FFFFFF',
                        fontSize: '2rem',
                        fontWeight: 700,
                        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif"
                    }}
                >
                    ${pkg.priceUsd || 0}
                </Typography>
            </Box>

            {/* Tokens */}
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 0.5,
                    padding: '8px 12px',
                    backgroundColor: 'rgba(124, 58, 237, 0.15)',
                    borderRadius: '8px'
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <BoltIcon sx={{ color: '#a855f7', fontSize: '1rem' }} />
                    <Typography
                        sx={{
                            color: '#a855f7',
                            fontSize: '0.875rem',
                            fontWeight: 600
                        }}
                    >
                        {(pkg.tokens || 0).toLocaleString()} tokens
                    </Typography>
                </Box>
                {hasBonus && (
                    <Typography
                        sx={{
                            color: '#27B56D',
                            fontSize: '0.75rem',
                            fontWeight: 600
                        }}
                    >
                        +{pkg.bonusTokens || 0} bonus!
                    </Typography>
                )}
            </Box>

            {/* Price per token */}
            <Typography
                sx={{
                    color: 'rgba(255, 255, 255, 0.4)',
                    fontSize: '0.75rem',
                    textAlign: 'center',
                    marginTop: 1
                }}
            >
                {pricePerToken}¢ per token
            </Typography>
        </Box>
    );
};

/**
 * Token Usage Row Component
 */
const TokenUsageRow = ({ label, cost }) => (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '0.8125rem' }}>
            {label}
        </Typography>
        <Typography sx={{ color: '#a855f7', fontSize: '0.8125rem', fontWeight: 600 }}>
            {cost} tokens
        </Typography>
    </Box>
);

export default PricingModal;
